import React, { useEffect, useState } from 'react';
import { Button, InputNumber, Popover, Select, Space, Typography } from 'antd';
import { DraftRow } from '../hooks/useDirtyBuffer';

interface QuickResultPopoverProps {
  open: boolean;
  onClose: () => void;
  /** Row che ha ricevuto il focus quando l'operatore ha premuto G. */
  anchorRow: DraftRow | null;
  /** Tenant row (Teramo) — required to know which side the goals belong to. */
  tenantRow: DraftRow | null;
  /** All non-deleted rows, for the case "G premuto sulla riga tenant" → serve picker. */
  allRows: DraftRow[];
  /** Callback con risultato pronto: applica delta a entrambe le righe. */
  onConfirm: (opponent: DraftRow, tenantGoals: number, opponentGoals: number) => void;
}

/**
 * Quick-result macro (§4.1 spec): aggiorna PG/V/N/P/GF/GS in una sola "commit"
 * sia per la riga tenant che per l'avversario. NB: non crea record Match
 * (out-of-scope) — solo classifica.
 */
export function QuickResultPopover({
  open,
  onClose,
  anchorRow,
  tenantRow,
  allRows,
  onConfirm,
}: QuickResultPopoverProps): React.ReactElement | null {
  const [tenantGoals, setTenantGoals] = useState<number>(0);
  const [opponentGoals, setOpponentGoals] = useState<number>(0);
  const [opponentUid, setOpponentUid] = useState<string | undefined>(undefined);

  // Se l'anchor non e' tenant, l'avversario e' l'anchor stesso. Altrimenti picker.
  const opponentNeedsPick = anchorRow == null || anchorRow.isOwnTeam;
  const resolvedOpponent: DraftRow | null = opponentNeedsPick
    ? allRows.find((r) => r.uid === opponentUid) ?? null
    : anchorRow;

  useEffect(() => {
    if (!open) return;
    setTenantGoals(0);
    setOpponentGoals(0);
    setOpponentUid(undefined);
  }, [open]);

  if (!open || !anchorRow) return null;
  if (!tenantRow) {
    return (
      <Popover
        open
        onOpenChange={(v) => !v && onClose()}
        content={
          <div style={{ padding: 8 }}>
            <Typography.Text type="warning">
              Imposta prima una squadra come Tenant (icona stella nella riga).
            </Typography.Text>
          </div>
        }
      >
        <span />
      </Popover>
    );
  }

  const submit = (): void => {
    if (!resolvedOpponent) return;
    onConfirm(resolvedOpponent, tenantGoals, opponentGoals);
    onClose();
  };

  const opponentLabel = resolvedOpponent?.teamName ?? '—';

  return (
    <Popover
      open
      destroyTooltipOnHide
      onOpenChange={(v) => !v && onClose()}
      placement="rightTop"
      content={
        <div
          style={{ minWidth: 320 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              submit();
            } else if (e.key === 'Escape') {
              e.stopPropagation();
              onClose();
            }
          }}
        >
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            Risultato vs {opponentLabel}
          </Typography.Title>
          {opponentNeedsPick ? (
            <div style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Avversario
              </Typography.Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                showSearch
                placeholder="Seleziona avversario"
                optionFilterProp="label"
                value={opponentUid}
                onChange={setOpponentUid}
                options={allRows
                  .filter((r) => !r.isOwnTeam && !r.isDeleted)
                  .map((r) => ({ value: r.uid, label: r.teamName }))}
              />
            </div>
          ) : null}
          <Space align="center" size={8}>
            <Typography.Text strong>{tenantRow.teamName}</Typography.Text>
            <InputNumber
              autoFocus
              min={0}
              max={99}
              value={tenantGoals}
              onChange={(v) => setTenantGoals(Number(v ?? 0))}
              style={{ width: 64 }}
            />
            <Typography.Text>−</Typography.Text>
            <InputNumber
              min={0}
              max={99}
              value={opponentGoals}
              onChange={(v) => setOpponentGoals(Number(v ?? 0))}
              style={{ width: 64 }}
            />
            <Typography.Text strong>{opponentLabel}</Typography.Text>
          </Space>
          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>Annulla</Button>
              <Button type="primary" onClick={submit} disabled={!resolvedOpponent}>
                Conferma (Enter)
              </Button>
            </Space>
          </div>
        </div>
      }
    >
      <span />
    </Popover>
  );
}
